import type { ProductPostingPlatform } from "@/components/catalog/ProductPostingAdDialog";
import { computeLandedCost } from "@/lib/catalog/purchaseCost";
import type { ProductAdListingRow } from "@/lib/catalog/productAdListingsStore";
import type { ProductDraft } from "@/lib/catalog/types";

export function productPostingPlatformToApi(platform: ProductPostingPlatform): ProductAdListingRow["platform"] {
  if (platform === "pakwheels") return "pak-wheels";
  if (platform === "wisewheels") return "wise-wheels";
  return "olx";
}

function formatPKR(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return `₨ ${Number(n).toLocaleString()}`;
}

export type MarketplaceAdPostSource = Pick<
  ProductDraft,
  "name" | "categoryId" | "brandId" | "description" | "productImages" | "purchasePrice" | "purchaseCurrency" | "purchaseCostLines" | "landedCost"
> & { id: string };

export type MarketplaceAdPostPayload = {
  platform: ProductPostingPlatform;
  productId: string;
  headline: string;
  imageUrl?: string | null;
  price: string;
  location: string;
  description: string;
  existingListing: ProductAdListingRow | null;
};

export function buildMarketplaceAdPostPayload(
  source: MarketplaceAdPostSource,
  platform: ProductPostingPlatform,
  options: {
    canViewPrices: boolean;
    categoryNameById: Map<string, string>;
    brandNameById: Map<string, string>;
    listingsForProduct: ProductAdListingRow[];
  },
): MarketplaceAdPostPayload {
  const apiPlat = productPostingPlatformToApi(platform);
  const existingListing = options.listingsForProduct.find((r) => r.platform === apiPlat) ?? null;

  const landed = source.landedCost ?? computeLandedCost(source.purchasePrice, source.purchaseCostLines);
  const priceNum = landed ?? source.purchasePrice ?? null;
  let priceStr: string;
  if (!options.canViewPrices) {
    priceStr = "—";
  } else if (source.purchaseCurrency && source.purchaseCurrency !== "PKR") {
    priceStr = `${source.purchaseCurrency} ${priceNum != null ? Number(priceNum).toLocaleString() : "—"}`;
  } else {
    priceStr = formatPKR(priceNum);
  }
  const parts: string[] = [];
  const cat = options.categoryNameById.get(source.categoryId);
  if (cat) parts.push(cat);
  if (source.brandId) {
    const b = options.brandNameById.get(source.brandId);
    if (b) parts.push(b);
  }
  const location = parts.length ? parts.join(" · ") : "—";
  const description =
    source.description?.trim() ||
    [source.name, location !== "—" ? `Listed under: ${location}.` : null].filter(Boolean).join(" ");

  return {
    platform,
    productId: source.id,
    headline: source.name,
    imageUrl: source.productImages?.[0]?.url,
    price: priceStr,
    location,
    description: description || source.name,
    existingListing,
  };
}
